// Action Creators
// Constants
import { ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { LogicModule } from "@ms/planner-logicmodules/lib/local/modules/LogicModule";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Utilities
import { checkIfNonNativeViewFromSubRouteType, extractPlanRoutingDetailsFromSubRoute, extractPrimaryViewIdFromRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
/**
 * ActionCreator for performance data collection like EUPL and PLT, and the interaction tracking
 * lifecycle. The interaction tracking decisions (start, complete, cancel) are owned by the logic
 * module; this shell forwards the exact contract each caller passed.
 */ export class PerformanceCollectionActionCreator {
    appStart(pageName, appStartTime) {}
    pageReady(dataFetchStart, dataFetchEnd, exportName, metaTag, extraData) {
        this.logViewRenderPerfMarkerForUnoNonNativeViews();
    }
    /**
     * Begin tracking a user interaction. A shell over the logic module, which owns the arming
     * decision; this forwards the exact contract the caller passed.
     * @param interactionId Which interaction this is and what it was performed on
     * @param targetViewId The view whose response completes this interaction
     * @param entryPoint Where the user triggered it from
     * @param extraData Optional further context logged with the measurement
     */ startInteraction(interactionId, targetViewId, entryPoint, extraData) {
        this.modules.load(LogicModule.PerformanceCollection).startInteraction(interactionId, targetViewId, entryPoint, extraData);
    }
    /**
     * Records how far a view has rendered, completing anything that render finished. The state is
     * handed over rather than read back, since the write below is dispatched asynchronously.
     */ viewRender(viewDetails, state, extraData) {
        const module = this.modules.load(LogicModule.PerformanceCollection);
        module.completeInteractions(viewDetails, state);
        module.setViewRenderState(viewDetails, state, extraData);
    }
    /**
     * Signal that something happened on a view, completing everything it finished. A shell over the
     * logic module, which owns the completion decision; this forwards the exact contract the caller
     * passed.
     * @param targetViewId The view something happened on
     * @param renderState How far that view has rendered, handed over by the render signal. Null when
     * what happened was not a render, such as a repaint.
     */ completeInteractions(targetViewId, renderState) {
        this.modules.load(LogicModule.PerformanceCollection).completeInteractions(targetViewId, renderState);
    }
    /**
     * Cancel tracking for a view, logging anything still pending as abandoned. A shell over the logic
     * module, which owns the cancel decision; this forwards the exact contract the caller passed.
     */ cancelInteractions(targetViewId) {
        this.modules.load(LogicModule.PerformanceCollection).cancelInteractions(targetViewId);
    }
    /** Logs view render perf marker for UNO non-native views on page ready. */ logViewRenderPerfMarkerForUnoNonNativeViews() {
        const userId = this.storeLoader.load(Store.AppContext).getAppContext().userId ?? "";
        const routeStore = this.storeLoader.load(Store.Route);
        const currentSubRouteType = routeStore.getCurrentSubRouteType();
        const currentSubRoute = routeStore.getCurrentSubRoute();
        // Don't log ViewRender marker on pageReady for native views
        if (!checkIfNonNativeViewFromSubRouteType(currentSubRouteType, this.configProvider)) {
            return;
        }
        this.viewRender(extractPrimaryViewIdFromRoute(currentSubRouteType, currentSubRoute, userId), ViewRenderState.Critical, {
            viewType: `${extractPlanRoutingDetailsFromSubRoute(currentSubRoute, currentSubRouteType)?.viewType}`
        });
    }
    /**
     * Initializes a new instance of the performance collector action creator
     * @param modules The logic module providers the SPA uses to get logic modules
     * @param storeLoader Store loader for reading store state
     * @param configProvider Configuration provider for feature flags
     */ constructor(modules, storeLoader, configProvider){
        this.modules = modules;
        this.storeLoader = storeLoader;
        this.configProvider = configProvider;
    }
}
